"use client";

import { useState, type FormEvent } from "react";
import Link from "next/link";
import { Check, Phone } from "lucide-react";
import { useLang } from "@/contexts/language-context";
import { normalizeUsPhone, type PartnerDoor } from "@/lib/partners/leads";
import { trackLead } from "@/lib/marketing/pixelEvents";

/**
 * Formulario interno de las landings de captación (/partners y /agentes).
 * Reemplaza al formulario de GoHighLevel embebido: el lead nace en Redy
 * (POST /api/partners/leads → partner_leads + ficha en el pipeline + aviso +
 * webhook de GHL para el SMS). Nombre y teléfono obligatorios, casilla de
 * contacto obligatoria; lo demás, opcional.
 *
 * Puerta `referidor` = registro propio: además muestra el restaurante que la
 * persona quiere presentar (todo opcional) y la casilla OBLIGATORIA de los
 * términos del programa (/partners/terminos). El servidor la exige.
 */
type Status = "idle" | "busy" | "done" | "error";
type ErrorKind = "phone" | "name" | "consent" | "terms" | "email" | "businessPhone" | "network" | "unavailable" | null;

const inputCls =
  "h-[52px] w-full min-w-0 rounded-2xl border border-black/10 bg-[#FAFAFA] px-4 text-[15px] text-redy-charcoal placeholder:text-redy-secondary/70 outline-none transition-all focus:border-redy-orange focus:ring-2 focus:ring-redy-orange/20 focus:bg-white";

export default function PartnerLeadForm({ door, referralsPublicEnabled = false }: { door: PartnerDoor; referralsPublicEnabled?: boolean }) {
  const { lang, t } = useLang();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", note: "", website: "", consent: false,
    businessName: "", businessOwner: "", businessPhone: "", terms: false,
  });
  // Registro propio del programa: solo con el flag público encendido. Apagado, la
  // puerta referidor es el formulario de siempre ("quiero que me llamen").
  const esReferidor = door === "referidor" && referralsPublicEnabled;
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<ErrorKind>(null);

  const noteLabel =
    door === "agente"
      ? t("What have you sold before? (optional)", "¿Qué has vendido antes? (opcional)")
      : t("Which restaurant owners do you know? (optional)", "¿Qué dueños de restaurante conoces? (opcional)");
  const notePlaceholder =
    door === "agente"
      ? t("Insurance, cars, phones, nothing yet…", "Seguros, carros, celulares, nada todavía…")
      : t("Two taquerías in Oak Cliff, my brother-in-law's place…", "Dos taquerías en Oak Cliff, el local de mi cuñado…");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (status === "busy") return;
    setError(null);
    if (form.name.trim().length < 2) return setError("name");
    if (!normalizeUsPhone(form.phone)) return setError("phone");
    if (esReferidor && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError("email");
    if (!form.consent) return setError("consent");
    if (esReferidor && form.businessPhone.trim() && !normalizeUsPhone(form.businessPhone)) return setError("businessPhone");
    if (esReferidor && !form.terms) return setError("terms");
    setStatus("busy");
    try {
      const res = await fetch("/api/partners/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          door,
          name: form.name,
          phone: form.phone,
          email: form.email,
          note: form.note,
          consent_contact: form.consent,
          lang,
          website: form.website,
          ...(esReferidor
            ? {
                program_terms: form.terms,
                business_name: form.businessName,
                business_owner: form.businessOwner,
                business_phone: form.businessPhone,
              }
            : {}),
        }),
      });
      if (res.ok) {
        trackLead({ content_name: door, content_category: door === "agente" ? "agentes" : "partners" });
        setStatus("done");
        return;
      }
      if (res.status === 400) {
        const body = (await res.json().catch(() => null)) as { fields?: string[] } | null;
        const f = body?.fields ?? [];
        setError(
          f.includes("phone") ? "phone"
            : f.includes("name") ? "name"
            : f.includes("email") ? "email"
            : f.includes("consent_contact") ? "consent"
            : f.includes("program_terms") ? "terms"
            : f.includes("business_phone") ? "businessPhone"
            : "network",
        );
      } else if (res.status === 429) {
        // Ya recibimos este número hace poco: para la persona es lo mismo que «listo».
        setStatus("done");
        return;
      } else if (res.status === 503) {
        setError("unavailable");
      } else {
        setError("network");
      }
      setStatus("error");
    } catch {
      setError("network");
      setStatus("error");
    }
  }

  if (status === "done") {
    const first = form.name.trim().split(/\s+/)[0];
    const e164 = normalizeUsPhone(form.phone);
    const last4 = e164 ? e164.slice(-4) : "";
    return (
      <div className="p-8 sm:p-10 text-center" role="status" aria-live="polite">
        <div
          className="mx-auto w-16 h-16 rounded-full grid place-items-center shadow-[0_12px_32px_rgba(215,38,61,0.25)]"
          style={{ background: "linear-gradient(135deg,#D7263D,#F4722B)" }}
        >
          <Check size={30} strokeWidth={3} color="#fff" />
        </div>
        <h3 className="mt-6 text-3xl font-black tracking-tight text-redy-charcoal">
          {t(`Done, ${first}.`, `Listo, ${first}.`)}
        </h3>
        <p className="mt-3 text-[16px] text-redy-secondary leading-relaxed max-w-md mx-auto">
          {esReferidor
            ? t(
                "We received your request. We review every partner by hand and email you when your code is active.",
                "Recibimos tu solicitud. Revisamos cada socio a mano y te avisamos por correo cuando tu código esté activo."
              )
            : door === "agente"
            ? t(
                `I'll call you at the number ending in ${last4} during business hours. Fifteen minutes.`,
                `Te llamo al número que termina en ${last4} en horario de oficina. Quince minutos.`
              )
            : t(
                `We'll call you at the number ending in ${last4} during business hours. Fifteen minutes.`,
                `Te llamamos al número que termina en ${last4} en horario de oficina. Quince minutos.`
              )}
        </p>
        {esReferidor ? null : (
          <div className="mt-6 inline-flex items-center gap-2 bg-[#FAFAFA] border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal">
            <Phone size={13} />
            {t("You'll get a text first so you know it's us", "Antes te llega un mensaje para que sepas que somos nosotros")}
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="p-6 sm:p-8 space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{t("Your name", "Tu nombre")}</span>
          <input
            className={inputCls}
            name="name"
            autoComplete="name"
            required
            minLength={2}
            maxLength={80}
            placeholder={t("First and last name", "Nombre y apellido")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            aria-invalid={error === "name"}
          />
        </label>
        <label className="block">
          <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{t("Mobile phone (US)", "Teléfono móvil (EE. UU.)")}</span>
          <input
            className={inputCls}
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            placeholder="(214) 555-0199"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            aria-invalid={error === "phone"}
          />
        </label>
      </div>
      <label className="block">
        <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{esReferidor ? t("Email", "Correo") : t("Email (optional)", "Correo (opcional)")}</span>
        <input
          className={inputCls}
          name="email"
          type="email"
          autoComplete="email"
          maxLength={254}
          required={esReferidor}
          aria-invalid={error === "email"}
          placeholder="tu@correo.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{noteLabel}</span>
        <textarea
          className={`${inputCls} h-auto min-h-[88px] py-3 resize-none`}
          name="note"
          rows={3}
          maxLength={500}
          placeholder={notePlaceholder}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
      </label>
      {/* Honeypot: un humano no lo ve. */}
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />

      {esReferidor ? (
        <fieldset className="rounded-2xl border border-black/10 p-4 space-y-4">
          <legend className="px-2 text-[12px] font-bold tracking-wide text-redy-charcoal">
            {t("The restaurant you want to introduce (optional)", "El restaurante que quieres presentar (opcional)")}
          </legend>
          <label className="block">
            <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{t("Restaurant name", "Nombre del restaurante")}</span>
            <input
              className={inputCls}
              name="business_name"
              maxLength={160}
              placeholder={t("Taquería Lupe", "Taquería Lupe")}
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            />
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{t("Owner's name", "Nombre del dueño")}</span>
              <input
                className={inputCls}
                name="business_owner"
                maxLength={120}
                value={form.businessOwner}
                onChange={(e) => setForm({ ...form, businessOwner: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="block text-[12px] font-bold tracking-wide text-redy-charcoal mb-1.5">{t("Restaurant phone", "Teléfono del restaurante")}</span>
              <input
                className={inputCls}
                name="business_phone"
                type="tel"
                inputMode="tel"
                placeholder="(972) 555-0123"
                value={form.businessPhone}
                onChange={(e) => setForm({ ...form, businessPhone: e.target.value })}
                aria-invalid={error === "businessPhone"}
              />
            </label>
          </div>
          <p className="text-[12px] text-redy-secondary leading-relaxed">
            {t(
              "Add the restaurant name and phone and we register it under your name. You can skip this and do it later.",
              "Pon el nombre y el teléfono del restaurante y lo registramos a tu nombre. Puedes dejarlo en blanco y hacerlo después."
            )}
          </p>
        </fieldset>
      ) : null}

      <label className={`flex gap-3 items-start rounded-2xl border p-4 cursor-pointer transition-colors ${error === "consent" ? "border-redy-red bg-redy-red/[0.03]" : "border-black/10 bg-[#FAFAFA] has-[:checked]:border-redy-orange/60 has-[:checked]:bg-white"}`}>
        <input
          type="checkbox"
          className="mt-0.5 w-[18px] h-[18px] shrink-0 accent-[#F4722B]"
          checked={form.consent}
          onChange={(e) => setForm({ ...form, consent: e.target.checked })}
          aria-invalid={error === "consent"}
        />
        <span className="text-[13px] text-redy-secondary leading-relaxed">
          {t(
            "I agree that Redy may call me and text me at this number about this program. Message frequency varies; reply STOP to opt out.",
            "Acepto que Redy me llame y me escriba por mensaje de texto a este número sobre este programa. La frecuencia varía; responde STOP para dejar de recibirlos."
          )}
        </span>
      </label>

      {esReferidor ? (
        <label className={`flex gap-3 items-start rounded-2xl border p-4 cursor-pointer transition-colors ${error === "terms" ? "border-redy-red bg-redy-red/[0.03]" : "border-black/10 bg-[#FAFAFA] has-[:checked]:border-redy-orange/60 has-[:checked]:bg-white"}`}>
          <input
            type="checkbox"
            className="mt-0.5 w-[18px] h-[18px] shrink-0 accent-[#F4722B]"
            checked={form.terms}
            onChange={(e) => setForm({ ...form, terms: e.target.checked })}
            aria-invalid={error === "terms"}
          />
          <span className="text-[13px] text-redy-secondary leading-relaxed">
            {t("I have read and accept the ", "Leí y acepto los ")}
            <Link href="/partners/terminos" target="_blank" rel="noopener" className="underline underline-offset-2 text-redy-charcoal font-semibold">
              {t("referral program terms", "términos del programa de referidos")}
            </Link>
            .
          </span>
        </label>
      ) : null}

      {error ? (
        <p className="text-[13px] font-semibold text-redy-red" role="alert">
          {error === "phone" && t("Enter a valid US mobile number.", "Escribe un número móvil válido de EE. UU.")}
          {error === "name" && t("Tell us your name.", "Dinos tu nombre.")}
          {error === "consent" && t("Check the box so we can call you.", "Marca la casilla para que podamos llamarte.")}
          {error === "email" && t("Enter your email so we can tell you when your code is active.", "Escribe tu correo para avisarte cuando tu código esté activo.")}
          {error === "terms" && t("Accept the program terms to continue.", "Acepta los términos del programa para continuar.")}
          {error === "businessPhone" && t("Enter a valid US phone for the restaurant, or leave it blank.", "Escribe un teléfono válido de EE. UU. para el restaurante, o déjalo en blanco.")}
          {error === "network" && t("Something failed. Try again in a moment.", "Algo falló. Inténtalo de nuevo en un momento.")}
          {error === "unavailable" && (
            <>
              {t("The form is being set up. ", "El formulario se está configurando. ")}
              <Link href="/contact" className="underline underline-offset-2">
                {t("Write to us and we call you.", "Escríbenos y te llamamos.")}
              </Link>
            </>
          )}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === "busy"}
        className="w-full h-14 rounded-full bg-redy-charcoal text-white font-bold text-[16px] hover:bg-black transition-all active:scale-[0.99] disabled:opacity-60 shadow-[0_12px_32px_rgba(0,0,0,0.18)]"
      >
        {status === "busy"
          ? t("Sending…", "Enviando…")
          : esReferidor
          ? t("Request my code", "Solicitar mi código")
          : t("Call me", "Quiero que me llamen")}
      </button>
      <p className="text-center text-[12px] text-redy-secondary">
        {t("No spam, no sales pressure. One call.", "Sin spam ni presión. Una llamada.")}
      </p>
    </form>
  );
}
